import React, { useEffect, useState, useCallback } from 'react';
import {
  Container, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, TextField, Dialog, DialogActions, DialogContent, DialogTitle, CircularProgress, Checkbox, FormControlLabel, Tabs, Tab
} from '@material-ui/core';
import config from '../../config';

const API_BASE = config.API_BASE;

function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userResults, setUserResults] = useState(null);
  const [userMessages, setUserMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openMessages, setOpenMessages] = useState(false);
  const [editUser, setEditUser] = useState({});
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'user' });
  const [error, setError] = useState('');
  const adminToken = localStorage.getItem('adminToken');
  const [userAttempts, setUserAttempts] = useState({});
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailDialogRecipients, setEmailDialogRecipients] = useState([]);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');
  const [emailError, setEmailError] = useState('');
  const [regStartDate, setRegStartDate] = useState('');
  const [regEndDate, setRegEndDate] = useState('');
  const [tab, setTab] = useState(0); // 0 = Active, 1 = Archived
  const [deleteDialogUser, setDeleteDialogUser] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const fetchUsersAndAttempts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/admin/users`, {
        headers: { 
          'Authorization': adminToken,
          'Content-Type': 'application/json'
        }
      });
      const usersData = await res.json();
      if (!res.ok) throw new Error(usersData.error || 'Failed to fetch users');
      setUsers(usersData);
      // Fetch attempts for all users
      const attemptsObj = {};
      for (const user of usersData) {
        const attemptsRes = await fetch(`${API_BASE}/results/${user.email}`, {
          headers: { 
            'Authorization': adminToken,
            'Content-Type': 'application/json'
          }
        });
        const attempts = await attemptsRes.json();
        attemptsObj[user.email] = attempts;
      }
      setUserAttempts(attemptsObj);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users or attempts');
    }
    setLoading(false);
  }, [adminToken]);

  useEffect(() => {
    fetchUsersAndAttempts();
  }, [fetchUsersAndAttempts]);

  const fetchUserResults = async (userId) => {
    setUserResults(null);
    setSelectedUser(userId);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${userId}/results`, {
        headers: { Authorization: adminToken }
      });
      const data = await res.json();
      setUserResults(data);
    } catch {
      setUserResults({ error: 'Failed to fetch results' });
    }
  };

  const fetchUserMessages = async (userId) => {
    setUserMessages([]);
    setSelectedUser(userId);
    setOpenMessages(true);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${userId}/messages`, {
        headers: { Authorization: adminToken }
      });
      const data = await res.json();
      setUserMessages(data);
    } catch {
      setUserMessages([{ error: 'Failed to fetch messages' }]);
    }
  };

  const handleCreateUser = async () => {
    setError('');
    try {
      const res = await fetch(`${API_BASE}/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: adminToken
        },
        body: JSON.stringify(newUser)
      });
      const data = await res.json();
      if (res.ok) {
        setOpenCreate(false);
        setNewUser({ name: '', email: '', password: '', role: 'user' });
        fetchUsersAndAttempts();
      } else {
        setError(data.error || 'Failed to create user');
      }
    } catch {
      setError('Failed to create user');
    }
  };

  const handleEditUser = (user) => {
    setEditUser({ ...user, password: '' });
    setOpenEdit(true);
  };

  const handleEditUserSave = async () => {
    setError('');
    try {
      const { _id, ...updateFields } = editUser;
      const res = await fetch(`${API_BASE}/admin/users/${_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: adminToken
        },
        body: JSON.stringify(updateFields)
      });
      const data = await res.json();
      if (res.ok) {
        setOpenEdit(false);
        fetchUsersAndAttempts();
      } else {
        setError(data.error || 'Failed to update user');
      }
    } catch {
      setError('Failed to update user');
    }
  };

  const handleArchiveUser = async (user, archived) => {
    setError('');
    try {
      const res = await fetch(`${API_BASE}/admin/users/${user._id}/archive`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: adminToken
        },
        body: JSON.stringify({ archived })
      });
      const data = await res.json();
      if (res.ok) {
        fetchUsersAndAttempts();
      } else {
        setError(data.error || 'Failed to update archive status');
      }
    } catch {
      setError('Failed to update archive status');
    }
  };

  const handleDeleteUserConfirm = async () => {
    if (!deleteDialogUser) return;
    setError('');
    setDeleteSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${deleteDialogUser._id}`, {
        method: 'DELETE',
        headers: { Authorization: adminToken }
      });
      const data = await res.json();
      if (res.ok) {
        setSelectedUsers((prev) => prev.filter((e) => e !== deleteDialogUser.email));
        setDeleteDialogUser(null);
        fetchUsersAndAttempts();
      } else {
        setError(data.error || 'Failed to delete user');
      }
    } catch {
      setError('Failed to delete user');
    }
    setDeleteSubmitting(false);
  };

  // Helper to check if user has taken all three tests
  const hasCompletedAllTests = (email) => {
    const attempts = userAttempts[email] || [];
    const testTypes = new Set(attempts.map(a => a.testType));
    return (
      testTypes.has('Learning Strategy Test') &&
      testTypes.has('Media Test') &&
      testTypes.has('Interactivity Test')
    );
  };

  // Date filtering helpers
  const filterByDate = (user) => {
    let regDate = user.createdAt ? new Date(user.createdAt) : null;
    if (regStartDate && regDate && regDate < new Date(regStartDate)) return false;
    if (regEndDate && regDate && regDate > new Date(regEndDate)) return false;
    return true;
  };

  // Checkbox select logic
  const handleUserSelect = (email) => {
    setSelectedUsers(prev => prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]);
  };
  const handleSelectAll = (checked) => {
    if (checked) setSelectedUsers(users.filter(filterByDate).map(u => u.email));
    else setSelectedUsers([]);
  };

  // Download selected users as CSV
  const handleDownloadCSV = () => {
    const csvRows = [
      ['Name', 'Email'],
      ...users.filter(u => selectedUsers.includes(u.email)).map(u => [u.name, u.email])
    ];
    const csvContent = csvRows.map(row => row.map(field => '"' + (field || '') + '"').join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'users.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Email dialog logic
  const openEmailDialog = (recipients) => {
    setEmailDialogRecipients(recipients);
    setEmailSubject('');
    setEmailBody('');
    setEmailSuccess('');
    setEmailError('');
    setEmailDialogOpen(true);
  };
  const handleSendEmail = async () => {
    setEmailSending(true);
    setEmailSuccess('');
    setEmailError('');
    try {
      const res = await fetch(`${API_BASE}/admin/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: adminToken },
        body: JSON.stringify({ recipients: emailDialogRecipients, subject: emailSubject, body: emailBody })
      });
      const data = await res.json();
      if (res.ok) setEmailSuccess('Email sent!');
      else setEmailError(data.error || 'Failed to send email');
    } catch {
      setEmailError('Failed to send email');
    }
    setEmailSending(false);
  };

  // Logout logic
  const handleLogout = () => {
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  // Filter users by tab (active/archived) and registration date
  const filteredUsers = users.filter(user => {
    if (tab === 0 && user.archived) return false;
    if (tab === 1 && !user.archived) return false;
    return filterByDate(user);
  });

  return (
    <Container>
      <Typography variant="h4" gutterBottom>Admin Dashboard</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} style={{marginBottom: 16}}>
        <Tab label="Active Users" />
        <Tab label="Archived Users" />
      </Tabs>
      <Button variant="contained" color="primary" onClick={() => setOpenCreate(true)} style={{marginRight: 8}}>
        Create New User
      </Button>
      <Button variant="outlined" color="secondary" onClick={handleDownloadCSV} style={{marginBottom: 8, marginRight: 8}} disabled={selectedUsers.length === 0}>
        Download Selected CSV
      </Button>
      <Button variant="outlined" color="primary" onClick={() => openEmailDialog(selectedUsers)} disabled={selectedUsers.length === 0} style={{marginBottom: 8, marginRight: 8}}>
        Send Email to Selected
      </Button>
      <Button variant="outlined" color="default" onClick={handleLogout} style={{marginBottom: 8}}>
        Logout
      </Button>
      <div style={{margin: '16px 0'}}>
        <TextField
          label="Reg. Start Date"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={regStartDate}
          onChange={e => setRegStartDate(e.target.value)}
          style={{marginRight: 8}}
        />
        <TextField
          label="Reg. End Date"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={regEndDate}
          onChange={e => setRegEndDate(e.target.value)}
          style={{marginRight: 16}}
        />
        <FormControlLabel
          control={<Checkbox checked={selectedUsers.length === users.filter(filterByDate).length && users.length > 0} onChange={e => handleSelectAll(e.target.checked)} />}
          label="Select All"
          style={{marginLeft: 24}}
        />
      </div>
      {error && <Typography color="error">{error}</Typography>}
      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper} style={{ marginTop: 20 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Archived</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map(user => (
                <TableRow key={user._id} style={!hasCompletedAllTests(user.email) ? { backgroundColor: '#fffbe6' } : {}}>
                  <TableCell>
                    <Checkbox checked={selectedUsers.includes(user.email)} onChange={() => handleUserSelect(user.email)} />
                  </TableCell>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : ''}</TableCell>
                  <TableCell>{user.archived ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    <Button variant="outlined" size="small" onClick={() => fetchUserResults(user._id)}>View Results</Button>{' '}
                    <Button variant="outlined" size="small" onClick={() => handleEditUser(user)}>Edit</Button>{' '}
                    {tab === 0 ? (
                      <Button variant="outlined" size="small" onClick={() => handleArchiveUser(user, true)}>Archive</Button>
                    ) : (
                      <Button variant="outlined" size="small" onClick={() => handleArchiveUser(user, false)}>Unarchive</Button>
                    )}{' '}
                    <Button variant="outlined" size="small" onClick={() => fetchUserMessages(user._id)}>View Messages</Button>{' '}
                    <Button variant="outlined" size="small" onClick={() => openEmailDialog([user.email])}>Send Email</Button>{' '}
                    <Button variant="outlined" size="small" color="secondary" onClick={() => setDeleteDialogUser(user)}>Delete</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* User Results Dialog */}
      <Dialog open={!!selectedUser && !!userResults} onClose={() => { setSelectedUser(null); setUserResults(null); }}>
        <DialogTitle>User Test Results</DialogTitle>
        <DialogContent>
          {userResults ? (
            userResults.error ? (
              <Typography color="error">{userResults.error}</Typography>
            ) : (
              Array.isArray(userResults) && userResults.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Test Type</TableCell>
                      <TableCell>Result</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userResults.map((attempt, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{attempt.testType}</TableCell>
                        <TableCell>{attempt.result}</TableCell>
                        <TableCell>{attempt.createdAt ? new Date(attempt.createdAt).toLocaleString() : ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography>No test attempts found.</Typography>
              )
            )
          ) : (
            <CircularProgress />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSelectedUser(null); setUserResults(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)}>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={editUser.name || ''}
            onChange={e => setEditUser({ ...editUser, name: e.target.value })}
            fullWidth margin="dense"
          />
          <TextField
            label="Email"
            value={editUser.email || ''}
            onChange={e => setEditUser({ ...editUser, email: e.target.value })}
            fullWidth margin="dense"
            type="email"
          />
          <TextField
            label="Password (leave blank to keep current)"
            value={editUser.password || ''}
            onChange={e => setEditUser({ ...editUser, password: e.target.value })}
            fullWidth margin="dense"
            type="password"
          />
          <TextField
            label="Role"
            value={editUser.role || 'user'}
            onChange={e => setEditUser({ ...editUser, role: e.target.value })}
            fullWidth margin="dense"
            select
            SelectProps={{ native: true }}
          >
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button onClick={handleEditUserSave} color="primary" variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* User Messages Dialog */}
      <Dialog open={openMessages} onClose={() => { setOpenMessages(false); setSelectedUser(null); }}>
        <DialogTitle>User Messages</DialogTitle>
        <DialogContent>
          {userMessages.length === 0 ? (
            <Typography>No messages found.</Typography>
          ) : userMessages[0].error ? (
            <Typography color="error">{userMessages[0].error}</Typography>
          ) : (
            userMessages.map((msg, idx) => (
              <Paper key={idx} style={{ margin: '10px 0', padding: 10 }}>
                <Typography variant="subtitle2">From: {msg.name} ({msg.email})</Typography>
                <Typography variant="body2">{msg.message}</Typography>
                <Typography variant="caption" color="textSecondary">{msg.createdAt ? new Date(msg.createdAt).toLocaleString() : ''}</Typography>
              </Paper>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenMessages(false); setSelectedUser(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete user confirmation */}
      <Dialog open={!!deleteDialogUser} onClose={() => !deleteSubmitting && setDeleteDialogUser(null)}>
        <DialogTitle>Delete user</DialogTitle>
        <DialogContent>
          <Typography>
            Permanently delete{' '}
            <strong>{deleteDialogUser ? deleteDialogUser.name || '(no name)' : ''}</strong>
            {' '}({deleteDialogUser ? deleteDialogUser.email : ''})? This removes their account, test results, and contact messages. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogUser(null)} disabled={deleteSubmitting}>Cancel</Button>
          <Button onClick={handleDeleteUserConfirm} color="secondary" variant="contained" disabled={deleteSubmitting}>
            {deleteSubmitting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={openCreate} onClose={() => setOpenCreate(false)}>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={newUser.name}
            onChange={e => setNewUser({ ...newUser, name: e.target.value })}
            fullWidth margin="dense"
          />
          <TextField
            label="Email"
            value={newUser.email}
            onChange={e => setNewUser({ ...newUser, email: e.target.value })}
            fullWidth margin="dense"
            type="email"
          />
          <TextField
            label="Password"
            value={newUser.password}
            onChange={e => setNewUser({ ...newUser, password: e.target.value })}
            fullWidth margin="dense"
            type="password"
          />
          <TextField
            label="Role"
            value={newUser.role}
            onChange={e => setNewUser({ ...newUser, role: e.target.value })}
            fullWidth margin="dense"
            select
            SelectProps={{ native: true }}
          >
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
          <Button onClick={handleCreateUser} color="primary" variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)}>
        <DialogTitle>Send Email</DialogTitle>
        <DialogContent>
          <Typography>To: {emailDialogRecipients.join(', ')}</Typography>
          <TextField
            label="Subject"
            value={emailSubject}
            onChange={e => setEmailSubject(e.target.value)}
            fullWidth margin="dense"
          />
          <TextField
            label="Message"
            value={emailBody}
            onChange={e => setEmailBody(e.target.value)}
            fullWidth margin="dense"
            multiline rows={4}
          />
          {emailSuccess && <Typography color="primary">{emailSuccess}</Typography>}
          {emailError && <Typography color="error">{emailError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSendEmail} color="primary" variant="contained" disabled={emailSending}>
            {emailSending ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default AdminDashboard; 